import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, GripVertical, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, BASE, uploadLogoFile } from "../../services/api";
import ImageUpload from "../../components/ui/ImageUpload";

const EMPTY = { name: "", logo_url: "", website: "", order: 0, active: true };

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

export default function AdminBrands() {
    const [brands, setBrands]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal]     = useState(null); // null | { mode: "add"|"edit", data }
    const [form, setForm]       = useState(EMPTY);
    const [saving, setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = async () => {
        setLoading(true);
        try { setBrands(await api.getBrandsAll()); }
        catch { toast.error("Nepodarilo sa načítať značky"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openAdd  = () => { setForm(EMPTY); setModal({ mode: "add" }); };
    const openEdit = (b) => { setForm({ name: b.name, logo_url: b.logo_url, website: b.website, order: b.order, active: b.active }); setModal({ mode: "edit", id: b.id }); };

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.name.trim()) { toast.error("Zadajte názov značky"); return; }
        setSaving(true);
        try {
            if (modal.mode === "add") {
                await api.createBrand(form);
                toast.success("Značka pridaná");
            } else {
                await api.updateBrand(modal.id, form);
                toast.success("Značka uložená");
            }
            setModal(null);
            await load();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Zmazať túto značku?")) return;
        setDeleting(id);
        try {
            await api.deleteBrand(id);
            setBrands(prev => prev.filter(b => b.id !== id));
            toast.success("Značka zmazaná");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setDeleting(null);
        }
    };

    const toggleActive = async (b) => {
        try {
            await api.updateBrand(b.id, { ...b, active: !b.active });
            setBrands(prev => prev.map(x => x.id === b.id ? { ...x, active: !x.active } : x));
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Značky techniky</h1>
                    <p className="text-zinc-500 text-sm mt-1">Zobrazujú sa priamo v hero sekcii (spodná lišta). Pre biely štýl nahrávajte PNG s priehľadným pozadím.</p>
                </div>
                <button onClick={openAdd}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors">
                    <Plus className="w-4 h-4" /> Pridať značku
                </button>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[1,2,3].map(n => <div key={n} className="h-16 bg-nova-surface/40 rounded-xl" />)}
                </div>
            ) : brands.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-zinc-600">
                    <div className="text-sm">Žiadne značky. Pridajte prvú.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {brands.map((b) => (
                        <div key={b.id}
                            className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors ${b.active ? "border-white/8 bg-nova-surface/40" : "border-white/4 bg-nova-surface/20 opacity-50"}`}>
                            <GripVertical className="w-4 h-4 text-zinc-700 shrink-0" />

                            {/* Logo preview */}
                            <div className="w-16 h-10 rounded-lg bg-nova-black/50 border border-white/8 flex items-center justify-center shrink-0 overflow-hidden">
                                {b.logo_url ? (
                                    <img src={resolveImg(b.logo_url)} alt={b.name}
                                        className="max-w-full max-h-full object-contain p-1" />
                                ) : (
                                    <span className="text-zinc-600 text-[10px] font-mono">logo</span>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="text-white text-sm font-semibold">{b.name}</div>
                                {b.website && <div className="text-zinc-500 text-xs truncate">{b.website}</div>}
                            </div>

                            <div className="font-mono text-[10px] text-zinc-600 shrink-0">#{b.order}</div>

                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => toggleActive(b)}
                                    title={b.active ? "Skryť" : "Zobraziť"}
                                    className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/8 transition-colors">
                                    {b.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <button onClick={() => openEdit(b)}
                                    className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/8 transition-colors">
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => del(b.id)} disabled={deleting === b.id}
                                    className="p-2 rounded-lg text-zinc-600 hover:text-red-400 transition-colors disabled:opacity-40">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModal(null)} />
                    <div className="relative w-full max-w-md bg-nova-surface border border-white/10 rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                            <h2 className="font-heading font-black text-white">
                                {modal.mode === "add" ? "Pridať značku" : "Upraviť značku"}
                            </h2>
                            <button onClick={() => setModal(null)} className="text-zinc-400 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Názov značky *</label>
                                <input value={form.name} onChange={e => set("name", e.target.value)}
                                    placeholder="napr. d&b audiotechnik"
                                    className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                            </div>

                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Logo</label>
                                <ImageUpload value={form.logo_url} onChange={v => set("logo_url", v)} uploadFn={uploadLogoFile} />
                                <p className="text-zinc-600 text-[10px] mt-1.5">Odporúčame PNG s priehľadným pozadím. Logo sa zobrazí v jednotnom bielom štýle.</p>
                            </div>

                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Web (voliteľné)</label>
                                <input value={form.website} onChange={e => set("website", e.target.value)}
                                    placeholder="https://..."
                                    className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Poradie</label>
                                    <input type="number" value={form.order} onChange={e => set("order", parseInt(e.target.value) || 0)}
                                        className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40" />
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Viditeľnosť</label>
                                    <button onClick={() => set("active", !form.active)}
                                        className={`w-full h-10 px-3 rounded-xl border text-sm font-medium transition-all ${form.active ? "border-nova-cyan/40 bg-nova-cyan/10 text-nova-cyan" : "border-white/8 bg-nova-black/50 text-zinc-500"}`}>
                                        {form.active ? "Zobrazená" : "Skrytá"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6 flex justify-end gap-2">
                            <button onClick={() => setModal(null)}
                                className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white text-sm transition-colors">
                                Zrušiť
                            </button>
                            <button onClick={save} disabled={saving}
                                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-nova-cyan text-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-50 transition-colors">
                                {saving ? "Ukladám…" : "Uložiť"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
